import { Box } from '@mui/material';

import {
  DiagramToolPreviewCard,
  DiagramToolRail,
  DiagramToolRailDivider,
  DiagramToolRailItem,
  DiagramToolRailMenu,
} from '@/components/diagrams/components/DiagramToolRail/DiagramToolRail';
import { ArrowConnectorIcon } from '@/components/icons/ArrowConnectorIcon';
import { MagicWand01Icon } from '@/components/icons/MagicWand01Icon';
import { StickerSquareIcon } from '@/components/icons/StickerSquareIcon';
import { TileIcon } from '@/components/icons/TileIcon';
import { TriangleIcon } from '@/components/icons/TriangleIcon';
import { UserCircleIcon } from '@/components/icons/UserCircleIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { getPublicImageUrl } from '@/utils/staticFiles';

import { useDiagramEditing } from '../../contexts/diagramEditing.context';
import { DiagramEditorTool } from './diagramEditorTools';

/** The folder of the preview images, exported from the Figma tooltips. */
const PREVIEW_IMAGE_DIR = '/diagramToolPreviews';

/** The Figma drawing of the element that a tool adds. */
function PreviewImage({ name }: { name: string }) {
  return (
    <Box
      component="img"
      src={getPublicImageUrl(`${PREVIEW_IMAGE_DIR}/${name}.png`)}
      alt=""
      sx={{ display: 'block', width: '100%' }}
    />
  );
}

/**
 * The tool rail at the left edge of the diagram builder canvas. A placement
 * tool adds its element at the next canvas click. The rail has no select
 * tool: a click on the current tool, the Escape key, or a placement returns
 * to selection. The rail is disabled while the canvas is locked.
 */
export function DiagramEditorToolRail() {
  const {
    tool,
    setTool,
    locked,
    canAddNode,
    resetPositions,
    reactFlowDiagram,
  } = useDiagramEditing();
  const toolProps = (value: DiagramEditorTool) => ({
    isActive: tool === value,
    onClick: () => setTool(tool === value ? DiagramEditorTool.Select : value),
    disabled: locked,
  });

  return (
    <DiagramToolRail>
      <DiagramToolRailItem
        label="Tile"
        icon={TileIcon}
        {...toolProps(DiagramEditorTool.Tile)}
        disabled={locked || !canAddNode}
        preview={
          <DiagramToolPreviewCard
            art={<PreviewImage name="tile" />}
            description="Add a card with an editable title, notes, footer, colors and more"
          />
        }
      />
      <DiagramToolRailItem
        label="Icon"
        icon={UserCircleIcon}
        {...toolProps(DiagramEditorTool.Icon)}
        disabled={locked || !canAddNode}
        preview={
          <DiagramToolPreviewCard
            art={<PreviewImage name="icon" />}
            description="Add an icon in a shape of your choice with a title, notes and optional label"
          />
        }
      />
      <DiagramToolRailItem
        label="Shape"
        icon={TriangleIcon}
        {...toolProps(DiagramEditorTool.Shape)}
        disabled={locked || !canAddNode}
        preview={
          <DiagramToolPreviewCard
            art={<PreviewImage name="shape" />}
            description="Add a shape of your choice with an editable title"
          />
        }
      />
      <DiagramToolRailItem
        label="Note"
        icon={StickerSquareIcon}
        {...toolProps(DiagramEditorTool.Text)}
        disabled={locked || !canAddNode}
        preview={
          <DiagramToolPreviewCard
            art={<PreviewImage name="text" />}
            description="Adds a note card where you can enter freeform text"
          />
        }
      />
      {/* To connect two tiles, the user drags from a connection handle. */}
      <DiagramToolRailItem
        label="Divider"
        icon={ArrowConnectorIcon}
        {...toolProps(DiagramEditorTool.Lines)}
        preview={
          <DiagramToolPreviewCard
            art={<PreviewImage name="lines" />}
            description="A freeform line or divider to separating content"
          />
        }
      />
      <DiagramToolRailDivider />
      <DiagramToolRailItem
        label="Layout"
        icon={MagicWand01Icon}
        disabled={locked}
        menu={
          <DiagramToolRailMenu aria-label="Layout">
            <MenuItem
              label="Automatically position tiles"
              onClick={() => {
                resetPositions();
                reactFlowDiagram.retryLayout();
              }}
            />
          </DiagramToolRailMenu>
        }
      />
    </DiagramToolRail>
  );
}
